import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {Observable} from 'rxjs';
import {ObjectBaseComponent} from 'features/core/shared/object/mvs-object-base/object-base.component';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {ObserverService} from 'features/core/shared/object/service/observer.service';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {CpRunReviewLookupService} from '../../../service/ql/cp-run-review-lookup.service';
import {CpRunMessageView} from '../../../interface/cp-run-message-view.interface';
import {CpRunMessageKind} from '../../../interface/cp-run-message-kind.interface';
import {
    CpRunMessageGuidance,
    getCpRunMessageGuidance,
    getCpRunSeverityLabel,
    getCpRunSeverityTag
} from '../../../service/domain/cp-run-message-guidance';
import {cpOpenContract} from '../../../service/domain/cp-object-navigation';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: ObjectBaseComponent
 * Reason: READ-ONLY detail of one run message — right pane of the tabs "Meldungen"
 *         (`cp.ProvisionContractRunMessage`) and "Lauf-Meldungen" (`cp.ProvisionRunMessage`).
 *         An ObjectBaseComponent on that message row; the object is retrieved through QL (see
 *         {@link getRetrieveObjectObservable}) so `dto` is the read-only message view.
 *
 * Replaces the generic CRUD form that used to open here. That form was editable (severity, code,
 * text, obsolete flag, delete) — a review must never change an engine message — and it hid the one
 * field that makes a message solvable, `detailedMessage`. Opening it also fired a form-template
 * refresh that the backend answered with 400 (`createdDate` of the message is a zoneless
 * LocalDateTime that the template endpoint cannot read back into the `Instant` of `DtoDetail`).
 * This pane therefore overrides the retrieval with the QL lookup: no CRUD read, no template
 * request, no save and no delete.
 *
 * What it shows, in the order the operator needs it:
 *   1. severity, code and the German "Was tun?" guidance of the code,
 *   2. message + detailed message,
 *   3. the contract (VSNR) with a read-only jump to the cm contract page,
 *   4. the agents — the one the message NAMES (M-004: Vermittler-Id from the detail) or else the
 *      contract's Hauptvermittler — each with Vermittler-Id and its Abrechnungskonto (or the
 *      explicit absence of one),
 *   5. event, German creation timestamp and the obsolete flag.
 *
 * Host contract: the review workbench binds `[messageId]` + `[messageKind]`; the setters turn them
 * into the standard `objectIdentifier`.
 */
@Component({
    selector: 'cp-run-detail-message',
    templateUrl: './cp-run-detail-message.component.html',
    styleUrls: ['./cp-run-detail-message.component.scss'],
    standalone: false
})
export class CpRunDetailMessageComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    private messageIdValue: number;
    private messageKindValue: CpRunMessageKind = 'contract';

    /** Id of the message row — sets {@link objectIdentifier}. */
    @Input()
    set messageId(id: number) {
        this.messageIdValue = id;
        this.syncObjectIdentifier();
    }

    /**
     * Falls back to {@link objectIdentifier}: when the object framework creates this pane
     * (`mvs-object` / openObjectViaDialog via the entity registry) it sets only the identifier.
     */
    get messageId(): number {
        return this.messageIdValue ?? this.objectIdentifier?.objectId ?? null;
    }

    /** `contract` = cp.ProvisionContractRunMessage, `run` = cp.ProvisionRunMessage. */
    @Input()
    set messageKind(kind: CpRunMessageKind) {
        this.messageKindValue = kind ?? 'contract';
        this.syncObjectIdentifier();
    }

    /** Derived from {@link objectIdentifier} when set — it is the truth for framework-created panes. */
    get messageKind(): CpRunMessageKind {
        const objectType = this.objectIdentifier?.objectType;
        if (objectType === 'cp.ProvisionRunMessage') {
            return 'run';
        }
        if (objectType === 'cp.ProvisionContractRunMessage') {
            return 'contract';
        }
        return this.messageKindValue;
    }

    /** The loaded message, typed. */
    get message(): CpRunMessageView {
        return this.dto as CpRunMessageView;
    }

    guidance: CpRunMessageGuidance;
    loadFailed: boolean;

    constructor(coreService: MvsCoreService,
                messageService: MvsMessageService,
                confirmationService: ConfirmationService,
                observerService: ObserverService,
                protected lookupService: CpRunReviewLookupService,
                protected navigationService: MvsObjectNavigationService) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    /**
     * Retrieve the message as the read-only QL view (message + contract + event + agents with their
     * Abrechnungskonto) instead of the CRUD read — see the class TSDoc.
     */
    protected getRetrieveObjectObservable(): Observable<any> {
        return this.messageKind === 'run'
            ? this.lookupService.loadRunMessage(this.objectIdentifier.objectId)
            : this.lookupService.loadContractMessage(this.objectIdentifier.objectId);
    }

    /** Drop a late answer for a row that is no longer selected (fast clicking in the worklist). */
    protected storeNewDto(dto: any): void {
        if (dto && this.objectIdentifier && dto.id != this.objectIdentifier.objectId) {
            return;
        }
        super.storeNewDto(dto);
    }

    /** The message is (re)loaded: resolve its "Was tun?" guidance. */
    onObjectChanged(): void {
        this.loadFailed = false;
        this.guidance = getCpRunMessageGuidance(this.message?.messageCode);
    }

    /** The message could not be loaded — show the error state with a retry. */
    postRefreshObjectError(error: any): void {
        super.postRefreshObjectError(error);
        this.guidance = null;
        this.loadFailed = true;
    }

    /** Reload the message (retry button). */
    retry(): void {
        this.loadFailed = false;
        if (this.objectIdentifier) {
            this.refreshObject(false);
        }
    }

    /** German label of the severity. */
    getSeverityLabel(severity: number): string {
        return getCpRunSeverityLabel(severity);
    }

    /** PrimeNG tag severity of the message severity. */
    getSeverityTag(severity: number): 'danger' | 'warn' | 'info' {
        return getCpRunSeverityTag(severity);
    }

    /** Caption above the agent list — says WHERE the agent comes from. */
    get agentCaption(): string {
        switch (this.message?.agentSource) {
            case 'message':
                return 'Vermittler laut Meldung';
            case 'account':
                return 'Betroffenes Abrechnungskonto';
            case 'contract':
                return 'Hauptvermittler des Vertrags';
            default:
                return 'Vermittler';
        }
    }

    /** Open the cm contract page of the message's contract, read-only. */
    openContract(): void {
        cpOpenContract(this.navigationService, this.message?.contractId);
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        // The host binds `messageId` / `messageKind`, not `objectIdentifier` — the base class does
        // not see them. First binding is handled by ngOnInit.
        const idChange = changes['messageId'];
        const kindChange = changes['messageKind'];
        const changed = (idChange && !idChange.firstChange) || (kindChange && !kindChange.firstChange);
        if (!changed) {
            return;
        }

        this.guidance = null;
        this.loadFailed = false;

        if (this.objectIdentifier) {
            this.refreshObject(false);
        } else {
            this.dto = null;
            this.initialized = false;
            this.busy = false;
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    /** Map the two host inputs onto the standard object identifier. */
    private syncObjectIdentifier(): void {
        const objectType = this.messageKindValue === 'run' ? 'cp.ProvisionRunMessage' : 'cp.ProvisionContractRunMessage';
        this.objectIdentifier = this.messageIdValue ? new ObjectIdentifier(objectType, this.messageIdValue) : null;
    }
}
